import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, tenantName);
      } else {
        await login(email, password);
      }
      navigate('/chat');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 423) setError('Kontot är låst. Försök igen senare.');
        else setError((err.data.message as string) ?? 'Inloggning misslyckades.');
      } else {
        setError('Kunde inte ansluta till servern.');
      }
    } finally {
      setLoading(false);
    }
  }

  const inputCls = "w-full rounded-lg border border-border bg-bg-tertiary px-3 py-3 sm:py-2.5 md:py-2 text-[16px] sm:text-[16px] md:text-[15px] focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20 transition-colors";

  return (
    <div className="flex h-full items-center justify-center bg-bg-primary p-4 sm:p-6 overflow-auto">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-accent">CLAW</h1>
          <p className="mt-1 text-[14px] sm:text-[14px] text-text-secondary">Ai Brick SaaS Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-bg-secondary p-5 sm:p-6">
          <h2 className="text-[17px] md:text-lg font-semibold">{isRegister ? 'Skapa konto' : 'Logga in'}</h2>

          {error && (
            <div className="rounded-lg bg-error/10 px-3 py-2.5 text-[14px] sm:text-[14px] text-error">{error}</div>
          )}

          {isRegister && (
            <div>
              <label className="mb-1.5 block text-[14px] sm:text-[14px] text-text-secondary">Organisationsnamn</label>
              <input type="text" value={tenantName} onChange={e => setTenantName(e.target.value)}
                required={isRegister} className={inputCls} placeholder="Mitt företag AB" />
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-[14px] sm:text-[14px] text-text-secondary">E-post</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              required autoComplete="email" className={inputCls} placeholder="namn@example.com" />
          </div>

          <div>
            <label className="mb-1.5 block text-[14px] sm:text-[14px] text-text-secondary">Lösenord</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              required minLength={8} autoComplete={isRegister ? 'new-password' : 'current-password'}
              className={inputCls} placeholder="Minst 8 tecken" />
          </div>

          <button type="submit" disabled={loading}
            className="w-full rounded-lg bg-accent py-3 sm:py-2.5 md:py-2 text-[16px] md:text-[15px] font-medium text-white
              transition hover:bg-accent-hover disabled:opacity-50
              active:scale-[0.98] touch-manipulation">
            {loading ? 'Vänta...' : isRegister ? 'Skapa konto' : 'Logga in'}
          </button>

          <p className="text-center text-[13px] text-text-secondary">
            {isRegister ? 'Har du redan konto?' : 'Inget konto?'}{' '}
            <button type="button" onClick={() => setIsRegister(!isRegister)}
              className="text-accent hover:underline touch-manipulation">
              {isRegister ? 'Logga in' : 'Registrera'}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
